import { useMemo } from 'react';
import styled from 'styled-components';
import LoadingSkeleton from 'framework-listing-lib/internal/shared/components/LoadingSkeleton';
import { generateRandomNumber } from 'framework-listing-lib/internal/utils/numberUtils';
import { DEFAULT_PADDING_TOKEN, PAGINATOR_HEIGHT, ROW_SIZE_EXTRA_SMALL, ROW_SIZE_MEDIUM, ROW_SIZE_SMALL } from 'framework-listing-lib/constants/sizes';
import { useHasFoldersTab } from 'framework-listing-lib/internal/Folders/hooks/useFolderProps';
import { useHasFoldersMode, useIsFoldersMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TableWrapper = styled.table.withConfig({
  displayName: "TableSkeleton__TableWrapper",
  componentId: "sc-1xseift-0"
})(["border-collapse:collapse;width:100%;padding:0;margin:0;"]);
const getBorderValue = ({
  theme
}) => `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}`;
const StyledTableHeader = styled.th.withConfig({
  displayName: "TableSkeleton__StyledTableHeader",
  componentId: "sc-1xseift-1"
})(["background:", ";height:44px;width:200px;border:", ";border-left:", ";border-right:", ";"], ({
  theme
}) => theme.color['fill-field-default'], getBorderValue, ({
  hasFoldersTab,
  isFoldersMode,
  theme
}) => hasFoldersTab && !isFoldersMode ? 'none' : getBorderValue({
  theme
}), ({
  hasFoldersTab,
  theme
}) => hasFoldersTab ? 'none' : getBorderValue({
  theme
}));
const StyledTableCell = styled.td.withConfig({
  displayName: "TableSkeleton__StyledTableCell",
  componentId: "sc-1xseift-2"
})(["background:", ";height:", "px;width:200px;border:", ";border-left:", ";border-right:", ";"], ({
  theme
}) => theme.color['fill-surface-default'], ({
  height
}) => height, getBorderValue, ({
  hasFoldersTab,
  isFoldersMode,
  theme
}) => hasFoldersTab && !isFoldersMode ? 'none' : getBorderValue({
  theme
}), ({
  hasFoldersTab,
  theme
}) => hasFoldersTab ? 'none' : getBorderValue({
  theme
}));
const TOTAL_COLUMNS = 5;
const TOTAL_ROWS = 15;
function getCellHeight({
  rowProps
}) {
  if (!rowProps || !rowProps.rowSize) {
    return ROW_SIZE_EXTRA_SMALL;
  }
  switch (rowProps.rowSize) {
    case 'extra-small':
      return ROW_SIZE_EXTRA_SMALL;
    default:
    case 'small':
      return ROW_SIZE_SMALL;
    case 'medium':
      return ROW_SIZE_MEDIUM;
  }
}
export default function TableSkeleton({
  rowProps
}) {
  const hasFoldersTab = useHasFoldersTab();
  const hasFoldersMode = useHasFoldersMode();
  const isFoldersMode = useIsFoldersMode();
  const cellHeight = useMemo(() => getCellHeight({
    rowProps
  }), [rowProps]);
  const headers = useMemo(() => Array.from({
    length: TOTAL_COLUMNS
  }).map(() => generateRandomNumber(30, 80)), []);
  const rows = useMemo(() => Array.from({
    length: TOTAL_ROWS
  }).map(() => Array.from({
    length: TOTAL_COLUMNS
  }).map(() => generateRandomNumber(30, 80))), []);
  return /*#__PURE__*/_jsxs(Flex, {
    "data-test-id": "table-skeleton",
    flexDirection: "column",
    alignItems: "center",
    inlineSize: "100%",
    children: [/*#__PURE__*/_jsxs(TableWrapper, {
      children: [/*#__PURE__*/_jsx("thead", {
        children: /*#__PURE__*/_jsx("tr", {
          children: headers.map((header, index) => /*#__PURE__*/_jsx(StyledTableHeader, {
            className: "skeleton-table-header",
            align: "center",
            hasFoldersMode: hasFoldersMode,
            hasFoldersTab: hasFoldersTab,
            isFoldersMode: isFoldersMode,
            children: /*#__PURE__*/_jsx(LoadingSkeleton, {
              blockSize: "20px",
              marginInlineStart: DEFAULT_PADDING_TOKEN,
              inlineSize: `${header}%`
            })
          }, index))
        })
      }), /*#__PURE__*/_jsx("tbody", {
        children: rows.map((_, rowIndex) => /*#__PURE__*/_jsx("tr", {
          children: rows[rowIndex].map((cell, cellIndex) => /*#__PURE__*/_jsx(StyledTableCell, {
            align: "center",
            hasFoldersMode: hasFoldersMode,
            hasFoldersTab: hasFoldersTab,
            height: cellHeight,
            isFoldersMode: isFoldersMode,
            children: /*#__PURE__*/_jsx(LoadingSkeleton, {
              blockSize: "20px",
              marginInlineStart: DEFAULT_PADDING_TOKEN,
              inlineSize: `${cell}%`
            })
          }, cellIndex))
        }, rowIndex))
      })]
    }), /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      justifyContent: "center",
      blockSize: `${PAGINATOR_HEIGHT}px`,
      marginBlockStart: "400",
      children: [/*#__PURE__*/_jsx(LoadingSkeleton, {
        blockSize: "20px",
        marginInlineStart: DEFAULT_PADDING_TOKEN,
        inlineSize: "180px"
      }), /*#__PURE__*/_jsx(LoadingSkeleton, {
        blockSize: "20px",
        marginInlineStart: DEFAULT_PADDING_TOKEN,
        inlineSize: "120px"
      })]
    })]
  });
}